import React from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../AuthContext';
import EnterpriseInquiry from '../components/EnterpriseInquiry';
import {
  KIT_TIERS, PILOT, PLATFORM_TIERS, PLATFORM_METERS, KIT_METERS, TEAM_ATTACH, appKit, kitBuild, kitBusiness, platformFree, platformStart, platformTeam, platformBusiness, dollars, perMonth,
} from '../data/plans';
import PageWorld from '../components/site/PageWorld';
import { PRICING } from '../data/worlds';
import { PageFilm } from '../components/PageFilm';
import PitchButton from '../components/PitchButton';

// Public pricing. Cards and dollars come from data/plans.js.

function TierGrid({ tiers }) {
  return (
    <div className="cs-land-price-grid">
      {tiers.map((t) => (
        <div key={t.name} className={`cs-sw-card ${t.featured ? 'cs-land-featured' : ''}`}>
          <h3>{t.name}</h3>
          <div className="cs-sw-price">{t.price}</div>
          <ul className="cs-product-specs">
            {t.bundle.map((b) => (
              <li key={b}>{b}</li>
            ))}
          </ul>
          <small>{t.who}</small>
          {t.href && (
            <div className="cs-land-cta-row" style={{ marginTop: 12 }}>
              <Link to={t.href} className={`cs-btn cs-btn-sm ${t.featured ? 'cs-btn-primary' : 'cs-btn-outline'}`}>
                {t.cta}
              </Link>
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

function MeterTable({ rows }) {
  return (
    <div className="cs-plan-tablewrap cs-land-compare">
      <table>
        <thead>
          <tr>
            <th>Meter</th>
            <th>Rate</th>
            <th>Note</th>
          </tr>
        </thead>
        <tbody>
          {rows.map(([meter, rate, note]) => (
            <tr key={meter}>
              <td><strong>{meter}</strong></td>
              <td className="cs-land-us">{rate}</td>
              <td>{note}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default function Pricing() {
  const { user } = useAuth();
  const member = !!user && !user.isAnonymous;
  return (
    <PageWorld world={PRICING}>
    <div className="cs-store">
      {member && (
        <p className="cs-store-note" style={{ textAlign: 'center', marginTop: 12 }}>
          Team review: <Link to="/pricing-internal">full internal spec →</Link>
        </p>
      )}
      <header className="cs-store-hero">
        <h1>Platform or App Kit</h1>
        <p>
          Already have ERP, CRM, or tickets? Buy Platform.
          Need an app your people log into? Rent App Kit.
          Need both? Listed bundles — not a silent upgrade.
        </p>
        <div className="cs-land-cta-row cs-land-cta-center">
          <a href="#platform" className="cs-btn cs-btn-primary">
            Already have the systems
          </a>
          <a href="#kit" className="cs-btn cs-btn-outline">
            Need a new app
          </a>
        </div>
      </header>

      <PageFilm
        base="/media/pricing-film"
        prefix="pricing"
        title="Platform or App Kit"
        summary="Two products. Both together is a written bundle — Kit and Platform Team, fifteen percent off the sum — not a hidden merge."
      />
      <PitchButton focus="pricing" label="Ask NoVA to walk the plans" hint="Interrupt with your own numbers." />

      <section className="cs-store-section">
        <div className="cs-land-two-products">
          <div className="cs-sw-card">
            <h3>Platform — nQube + seats + agentOS</h3>
            <p>
              You already have ERP / CRM / tickets / APIs. Done is a gated
              pipeline that writes into those systems.
            </p>
            <small>Priced against workflow tools.</small>
          </div>
          <div className="cs-sw-card">
            <h3>App Kit — instance + layer + Dev Console</h3>
            <p>
              You do not have the app you need. Done is a running instance
              people open — the public site first, then sign-in and the books
              on that same URL. The rent unlocks Dev Console: you change the
              app and ship it, on a foundation already built for the load,
              watched on every deploy, and updated with the rent. The fee
              is the license for that foundation.
            </p>
            <small>Priced against internal-app platforms.</small>
          </div>
        </div>
      </section>

      <section className="cs-store-section" id="platform">
        <h2>Platform</h2>
        <p className="cs-store-note" style={{ maxWidth: 860 }}>
          Shared nQube and production connectors. Start is {dollars(platformStart)} for 3,000
          workflow runs. Team is {dollars(platformTeam)} for 12,000. Business is {dollars(platformBusiness)}. None of
          those include a dedicated instance. Pilot is delivery of your first
          gated pipeline on a real system, $9,000–$24,000, not a plan row
          and not a first custom app.
        </p>
        <TierGrid tiers={PLATFORM_TIERS} />
        <p className="cs-land-micro" style={{ marginTop: 14 }}>
          Viewers stay free. Start, Team, and Business are priced in workflow
          runs. A run with one gated commit bills 4 Runtime Credits internally
          (run 2 + commit 2). Enterprise is quoted in those credits.
        </p>
        <div className="cs-land-cta-row">
          <Link to={platformStart.href} className="cs-btn cs-btn-primary">
            {platformStart.cta}
          </Link>
          <Link to={platformFree.href} className="cs-btn cs-btn-outline">
            {platformFree.cta}
          </Link>
          <a href={PILOT.href} className="cs-btn cs-btn-outline">
            {PILOT.label}
          </a>
          <a href="#enterprise" className="cs-btn cs-btn-outline">
            Enterprise review
          </a>
        </div>
      </section>

      <section className="cs-store-section" id="kit">
        <h2>App Kit</h2>
        <p className="cs-store-note" style={{ maxWidth: 860 }}>
          Kit at {perMonth(appKit)} is the instance: a URL, a database, and a layer
          repo, with Dev Console. Kit Site at {perMonth(kitBuild)} is a public site on a
          shared host and does not include that console. The monthly fee is
          the license for the foundation. A managed host is included. Your
          cloud or on-prem keeps the license and drops the host meters.
          nQube on that instance only is included. Service health and HTTP
          feature checks come with Kit. Browser feature checks and the
          monthly passive security baseline are the Assurance add-on — talk
          to us. Active security tests stay a confirmed nventr run.
          Org-wide pipelines into systems outside the app still belong on
          Platform. Kit Business is {dollars(kitBusiness)}. Viewers on an instance are
          unlimited.
        </p>
        <TierGrid tiers={KIT_TIERS} />
        <div className="cs-land-metered" style={{ marginTop: 14 }} id="native">
          <strong>Native app bundles (Kit and Kit Business add-ons).</strong>{' '}
          Desktop at $49/mo builds your app as macOS (.dmg) and Windows
          (.msi / installer) apps. Mobile at $79/mo builds iOS (.ipa for
          TestFlight and the App Store) and Android (.aab for Play). Same
          sign-in, same data, same release: the instance's web app wrapped in
          real native shells, built on hosted runners from App Builder,
          signed with your own certificates when you upload them. Store
          accounts and submissions stay yours; 20 builds a month per bundle.
        </div>
        <div className="cs-land-metered" style={{ marginTop: 14 }} id="lab">
          <strong>Lab (organization add-on, any plan with App Builder).</strong>{' '}
          $79/mo adds a studio, research, refine and test agent to every App
          Builder app in the organization. Talk an idea through with NoVA by
          voice: she interviews you, browses the web in a browser you watch
          live, and drafts a visual concept — problem, people, landscape,
          features, wireframed screens, flows — that you revise and accept
          before a line is built. Research searches the web server-side and
          files a draft brief with sources and proposals; Refine turns it
          into changes you apply with one click; Test drives your hosted app
          or its UAT twin in a real browser from a plain-language scenario,
          one screenshot per step, filed as a QA run. Steps that would change
          data on the live app wait for an admin's approval. Performance
          runs load your pages in the same browser and time your agent
          against budgets. Includes 30 research runs, 120 browser minutes
          and 20 concept images a month (concept drafts count as research
          runs; live browsing and performance draw on the same minutes);
          then $0.50 per research run, $0.10 per browser minute and $0.25
          per concept image.
        </div>
        <p className="cs-land-micro" style={{ marginTop: 14 }}>
          How the rented kit, layer repo, and 12-hour license work:{' '}
          <Link to="/app-kit">App Kit explainer →</Link>
        </p>
        <div className="cs-land-cta-row">
          <Link to={appKit.href} className="cs-btn cs-btn-primary">
            {appKit.cta}
          </Link>
          <a href="#enterprise" className="cs-btn cs-btn-outline">
            Enterprise review
          </a>
        </div>
      </section>

      <section className="cs-store-section" id="bundle">
        <h2>Both together — a written bundle, not a hidden merge-up</h2>
        <div className="cs-land-metered">
          <strong>Team attach: Kit {dollars(appKit)} + Platform Team {dollars(platformTeam)} → {TEAM_ATTACH.price}</strong>
          {' '}({TEAM_ATTACH.note}). You get the instance and org-wide nQube.
        </div>
        <div className="cs-land-metered" style={{ marginTop: 12 }}>
          <strong>Department attach: Kit Business $349 + Platform Business $599 → $825 / mo</strong>
          {' '}(13% off $948). Same rule, grown-up SKU.
        </div>
        <p className="cs-land-micro">
          Buying Kit alone does not unlock Platform for the org.
          Buying Platform alone does not give you a URL, database, or Dev
          Console. Platform is seats, credits, and optional VPC nodes.
          Kit is instance rent, the license, and Dev Console runs.
        </p>
        <div className="cs-land-cta-row">
          <a href="#enterprise" className="cs-btn cs-btn-primary">
            Tell us about a bundle
          </a>
        </div>
      </section>

      <section className="cs-store-section" id="enterprise">
        <h2>Enterprise</h2>
        <p className="cs-store-note" style={{ maxWidth: 860 }}>
          Platform Enterprise is typical year-1 from $8,000/mo. Kit Enterprise
          is from $3,000/mo. Neither is a self-serve checkout. Send a brief
          and we’ll spend 30 minutes on the first write.
        </p>
        <EnterpriseInquiry />
      </section>

      <section className="cs-store-section">
        <h2>Meters — split per product</h2>
        <div className="cs-land-two-products">
          <div>
            <h3>Platform</h3>
            <MeterTable rows={PLATFORM_METERS} />
          </div>
          <div>
            <h3>App Kit</h3>
            <MeterTable rows={KIT_METERS} />
          </div>
        </div>
        <p className="cs-land-micro">
          Bring your own model and the hosted-token line is $0. Seats and
          instance rent stay. Hosted model credits never stop your app: at a
          $0 balance hosted model calls pause with a clear 402 until you top
          up; everything else keeps running. Build and Run wallets stay separate so a week
          of coding agents cannot empty production, and a month of invoice
          intake cannot freeze the Dev Console.
        </p>
      </section>

      <footer className="cs-store-footer">
        Consumer appliance plans and Edge OS live with the hardware they
        attach to — the{' '}
        <Link to="/store">store</Link> and{' '}
        <Link to="/partners">partners</Link>. Hardware is not a third
        platform SKU.
      </footer>
    </div>
    </PageWorld>
  );
}
